/**
 * Reportes · Cruce ERP ↔ SNT — contrasta remitos del ERP contra series trazadas.
 * Útil para detectar remitos parciales o sin trazar.
 */

import { useMemo } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useRemitos, useOperaciones } from '@/api/queries';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtRemitoNumero } from '@/lib/format';

export function CruceTab() {
  const { data: remitos = [], isLoading } = useRemitos();
  const { data: operaciones = [] } = useOperaciones();

  const filas = useMemo(() => {
    return remitos.map((r) => {
      const op = operaciones.find((o) => o.remitoNumero === r.numero);
      const esperado = r.items.reduce((sum, it) => sum + it.cantidad, 0);
      const trazadas = op?.seriesIds.length ?? 0;
      const diff = esperado - trazadas;
      return {
        remito: r,
        op,
        esperado,
        trazadas,
        diff,
        estado: op
          ? op.anmat.estado === 'cancelada'
            ? ('cancelado' as const)
            : ('trazado' as const)
          : ('pendiente' as const),
      };
    });
  }, [remitos, operaciones]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando...
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="overflow-x-auto rounded border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Remito</TableHead>
                <TableHead>Esperado</TableHead>
                <TableHead>Trazado SNT</TableHead>
                <TableHead>Diff</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Op ANMAT</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((f) => (
                <TableRow key={f.remito.numero}>
                  <TableCell className="font-mono text-xs">
                    {fmtRemitoNumero(f.remito.numero)}
                  </TableCell>
                  <TableCell className="text-xs">{f.esperado}</TableCell>
                  <TableCell className="text-xs">{f.trazadas}</TableCell>
                  <TableCell
                    className={cn('text-xs font-bold', f.diff !== 0 && 'text-red-600')}
                  >
                    {f.diff === 0 ? '✓' : f.diff > 0 ? `−${f.diff}` : `+${-f.diff}`}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'badge',
                        f.estado === 'trazado'
                          ? 'badge-green'
                          : f.estado === 'cancelado'
                            ? 'badge-gray'
                            : 'badge-yellow',
                      )}
                    >
                      {f.estado === 'trazado'
                        ? 'Trazado'
                        : f.estado === 'cancelado'
                          ? 'Cancelado'
                          : 'Pendiente'}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-gray-600">
                    {f.op?.id ?? '—'}{' '}
                    {f.op?.anmat.codigoTransaccion && (
                      <>
                        <ArrowRight className="mx-0.5 inline h-2.5 w-2.5" />
                        {f.op.anmat.codigoTransaccion}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Alert className="mt-3 border-amber-200 bg-amber-50">
          <AlertDescription className="text-xs text-amber-900">
            Esta vista contrasta el N° de unidades del remito (input ERP) con las series
            efectivamente trazadas al SNT. Diferencias indican remitos parciales o sin trazar.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
